/**
 * Suggestion Card Component - Compact card for entry suggestions
 * Click to open review dialog, hover to see quick actions
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  LibraryBooks as LibraryIcon,
  PhotoLibrary as MediaIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { EntrySuggestion } from '@/types/api';
import { TelegramLinkButton } from '@/components/common/TelegramLinkButton';
import ConfidenceBadge from './ConfidenceBadge';

interface SuggestionCardProps {
  suggestion: EntrySuggestion;
  chatId: number;
  selected?: boolean;
  onToggleSelect?: (messageId: number) => void;
  onReview: (suggestion: EntrySuggestion) => void;
  onViewContext?: (messageId: number) => void;
  onMarkMissing?: (suggestion: EntrySuggestion) => void;
}

// Get color for pattern element type
const getPatternColor = (type: string): string => {
  switch (type.toUpperCase()) {
    case 'TITLE':
      return '#1976d2'; // primary blue
    case 'SEPARATOR':
      return '#ed6c02'; // warning orange
    case 'MEDIA':
      return '#2e7d32'; // success green
    default:
      return '#9e9e9e'; // grey
  }
};

// Parse pattern string into array of types
const parsePattern = (pattern: string): string[] => {
  return pattern.split('_').filter(Boolean);
};

// Get the first message ID based on pattern order
const getFirstMessageId = (suggestion: EntrySuggestion, patternParts: string[]): number => {
  if (patternParts.length === 0) return suggestion.title_message_id;

  const firstPart = patternParts[0].toUpperCase();
  switch (firstPart) {
    case 'SEPARATOR':
      return suggestion.separator_message_id || suggestion.title_message_id;
    case 'TITLE':
      return suggestion.title_message_id;
    case 'MEDIA':
      return suggestion.media_message_ids[0] || suggestion.title_message_id;
    default:
      return suggestion.title_message_id;
  }
};

const SuggestionCard: React.FC<SuggestionCardProps> = React.memo(({
  suggestion,
  chatId,
  selected = false,
  onToggleSelect,
  onReview,
  onViewContext,
  onMarkMissing,
}) => {
  const { t } = useTranslation('components');
  const hasLibraryMatch = suggestion.library_matches.length > 0;
  const bestLibraryMatch = suggestion.library_matches[0];
  const patternParts = parsePattern(suggestion.pattern);
  const firstMessageId = getFirstMessageId(suggestion, patternParts);
  const isIndexed = Boolean(suggestion.is_indexed);
  const isMissingMatch = Boolean(suggestion.is_missing_match) && !isIndexed;

  // Helper function to translate pattern elements
  const translatePatternLabel = (part: string): string => {
    return t(`customPatternBuilder.patternElementTypes.${part.toUpperCase()}`, part.toLowerCase());
  };

  // Helper function to translate full pattern
  const translatePattern = (pattern: string): string => {
    const parts = pattern.toUpperCase().split('_');
    const translatedParts = parts.map((part) =>
      t(`customPatternBuilder.patternElementTypes.${part}`, part)
    );
    return translatedParts.join(' → ');
  };

  // Build tooltip content with message IDs and text
  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 300 }}>
      {/* Title message */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'primary.light', fontWeight: 600 }}>
          TITLE #{suggestion.title_message_id}
        </Typography>
        {suggestion.title && (
          <Typography variant="caption" display="block" sx={{ direction: 'rtl', textAlign: 'right', opacity: 0.9 }}>
            {suggestion.title.length > 80 ? suggestion.title.substring(0, 80) + '...' : suggestion.title}
          </Typography>
        )}
      </Box>

      {/* Separator message */}
      {suggestion.separator_message_id && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: 'warning.light', fontWeight: 600 }}>
            SEPARATOR #{suggestion.separator_message_id}
          </Typography>
        </Box>
      )}

      {/* Media messages */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 600 }}>
          MEDIA × {suggestion.media_message_ids.length}
        </Typography>
        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
          IDs: {suggestion.media_message_ids.slice(0, 5).join(', ')}
          {suggestion.media_message_ids.length > 5 && ` +${suggestion.media_message_ids.length - 5} more`}
        </Typography>
      </Box>

      {/* Pattern */}
      <Typography variant="caption" display="block" sx={{ opacity: 0.7, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
        Pattern: {translatePattern(suggestion.pattern)}
      </Typography>
    </Box>
  );

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on action buttons
    const target = e.target as HTMLElement;
    if (
      target.closest('.hover-actions') ||
      target.closest('.edit-button') ||
      target.closest('button')
    ) {
      return;
    }
    // Click anywhere on card toggles selection
    onToggleSelect?.(suggestion.title_message_id);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReview(suggestion);
  };

  return (
    <Tooltip title={tooltipContent} placement="top" arrow enterDelay={400}>
      <Card
        onClick={handleCardClick}
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
          '&:hover .hover-actions': {
            opacity: 1,
          },
          border: selected ? 2 : 1,
          borderColor: isIndexed ? 'success.main' : (selected ? 'primary.main' : 'divider'),
        }}
      >
        <CardContent sx={{ p: 2, pb: '12px !important' }}>
          {/* Header Row: Checkbox + Confidence + Message ID + Hover Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                checked={selected}
                size="small"
                sx={{ p: 0.5, pointerEvents: 'none' }}
                tabIndex={-1}
              />
              <ConfidenceBadge score={suggestion.confidence} size="small" showLabel={false} />
              {/* First message ID */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem', fontWeight: 500 }}
              >
                #{firstMessageId}
              </Typography>
            </Box>

            {/* Edit button - always visible */}
            <Tooltip title={t('suggestionCard.editTooltip', 'Edit')} arrow>
              <IconButton
                className="edit-button"
                size="small"
                color="primary"
                onClick={handleEditClick}
                sx={{ p: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Hover Actions - Telegram & Context buttons */}
            <Box
              className="hover-actions"
              sx={{
                display: 'flex',
                gap: 0.5,
                opacity: 0,
                transition: 'opacity 0.2s ease',
              }}
            >
              <TelegramLinkButton
                chatId={chatId}
                messageId={suggestion.title_message_id}
                size="small"
              />
              {onViewContext && (
                <Tooltip title={t('suggestionCard.viewMessageTooltip', { messageId: suggestion.title_message_id })} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewContext(suggestion.title_message_id);
                    }}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Title row - Title + MS No */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                direction: 'rtl',
                textAlign: 'right',
                flex: 1,
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {suggestion.title || t('suggestionCard.noTitle')}
            </Typography>
            {/* MS No badge next to title */}
            {suggestion.ms_no && (
              <Typography
                variant="caption"
                sx={{
                  flexShrink: 0,
                  px: 0.75,
                  py: 0.25,
                  bgcolor: 'action.selected',
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                MS {suggestion.ms_no}
              </Typography>
            )}
          </Box>

          {isIndexed && (
            <Box sx={{ mb: 1 }}>
              <Tooltip
                title={
                  suggestion.existing_entry_id
                    ? t('suggestionCard.alreadyIndexedTooltip', { entryId: suggestion.existing_entry_id })
                    : t('suggestionCard.alreadyIndexed', 'Already Indexed')
                }
                arrow
              >
                <Chip
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={t('suggestionCard.alreadyIndexed', 'Already Indexed')}
                  color="success"
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
            </Box>
          )}

          {isMissingMatch && (
            <Box sx={{ mb: 1 }}>
              <Tooltip
                title={
                  suggestion.missing_entry_id
                    ? t('suggestionCard.alreadyMissingTooltip', {
                        entryId: suggestion.missing_entry_id,
                        matchType: suggestion.missing_match_type === 'ms_number'
                          ? t('suggestionCard.matchByMsNo', 'MS number')
                          : t('suggestionCard.matchByTitle', 'title'),
                      })
                    : t('suggestionCard.alreadyMissing', 'Already Marked Missing')
                }
                arrow
              >
                <Chip
                  icon={<WarningIcon fontSize="small" />}
                  label={t('suggestionCard.alreadyMissing', 'Already Marked Missing')}
                  color="warning"
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
            </Box>
          )}

          {/* Author - Below title */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              direction: 'rtl',
              textAlign: 'right',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {suggestion.author || '—'}
          </Typography>

          {/* Library match - own line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            {hasLibraryMatch ? (
              <>
                <LibraryIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{
                    direction: 'rtl',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {bestLibraryMatch.library_name}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="warning.main">
                {t('suggestionCard.noLibraryMatch')}
              </Typography>
            )}
          </Box>

          {/* Bottom row: Pattern pill + Media count */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
            {/* Pattern as a single pill with colored words */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  px: 0.75,
                  py: 0.25,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  textTransform: 'lowercase',
                }}
              >
                {patternParts.map((part, index) => (
                  <React.Fragment key={index}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: getPatternColor(part),
                        textTransform: 'lowercase',
                      }}
                    >
                      {translatePatternLabel(part)}
                    </Typography>
                    {index < patternParts.length - 1 && (
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.6rem', color: 'text.disabled', mx: '1px' }}
                      >
                        →
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>

            {/* Media count badge */}
            <Chip
              icon={<MediaIcon sx={{ fontSize: '14px !important' }} />}
              label={suggestion.media_ids.length}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' },
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
          </Box>

          {onMarkMissing && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkMissing(suggestion);
                }}
              >
                {t('suggestionCard.markMissing', 'Mark as Missing')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Tooltip>
  );
});

export default SuggestionCard;
